/**
 * App v0.3
 *
 * 構成（モードによって中央エリアと下部アクションが変わる）：
 *   - StatusBar       — ●LED + SCOUTER ON / 右側に MODE
 *   - 中央エリア:
 *       translate/summarize → Camera (4:5) + ScanLine
 *       interpret           → InterpretMode（StatusCard + 結果ブロック）
 *   - ModeSelector    — 「和訳 / 要約 / 通訳」3タブ
 *   - アクション行:
 *       translate/summarize → LOG / SHOT / SCAN / CONF
 *       interpret           → LOG / START or STOP / CONF
 *   - BottomDecor     — スピーカー穴 + SL-001
 *
 * モーダル類は v0.2 と同じ。スプラッシュは Phase G で sessionStorage 化予定。
 */

import { useEffect, useRef, useState } from 'react';

import { useApiKey } from './hooks/useApiKey';
import { useHistory } from './hooks/useHistory';
import { useInterpreter } from './hooks/useInterpreter';
import { useScanner } from './hooks/useScanner';
import { useSettings } from './hooks/useSettings';
import { ApiKeyModal } from './components/ApiKeyModal';
import { BottomDecor } from './components/BottomDecor';
import { Camera } from './components/Camera';
import { ControlButton } from './components/ControlButton';
import { FlashEffect } from './components/FlashEffect';
import { HistoryPanel } from './components/HistoryPanel';
import { InterpretMode } from './components/InterpretMode';
import { LiveToggle } from './components/LiveToggle';
import { ModeSelector } from './components/ModeSelector';
import { ResultPanel } from './components/ResultPanel';
import { ScanLine } from './components/ScanLine';
import { SettingsPanel } from './components/SettingsPanel';
import { ShotButton } from './components/ShotButton';
import { SplashScreen } from './components/SplashScreen';
import { StatusBar } from './components/StatusBar';

import packageJson from '../package.json';

import type { Mode } from './types';

import styles from './App.module.css';

const APP_VERSION = packageJson.version;

export function App() {
  const { apiKey, hasApiKey, saveApiKey } = useApiKey();
  const { settings, update: updateSettings } = useSettings();

  const [currentMode, setCurrentMode] = useState<Mode>('translate');

  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showApiKeyEditor, setShowApiKeyEditor] = useState(false);
  // スプラッシュは「タブ初回のみ」表示（v0.3+）
  const [splashDone, setSplashDone] = useState(
    () => sessionStorage.getItem('scouter-lens:splash-shown') === '1',
  );

  const handleSplashComplete = () => {
    sessionStorage.setItem('scouter-lens:splash-shown', '1');
    setSplashDone(true);
  };

  // 履歴はモード別に保存（v0.3+）
  const { history, append: appendHistory, clear: clearHistory } = useHistory(currentMode);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const {
    status: scannerStatus,
    latestResult,
    errorMessage: scannerError,
    isScanning,
    shoot,
    startScanning,
    stopScanning,
  } = useScanner({
    videoRef,
    mode: currentMode,
    apiKey,
    scanIntervalMs: settings.scanIntervalMs,
  });

  const interpreter = useInterpreter({
    apiKey,
    onSegmentTranslated: appendHistory,
  });

  // 画像系（translate/summarize）の新しい結果が出たら履歴に追加
  const lastSavedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (latestResult && latestResult.id !== lastSavedIdRef.current) {
      lastSavedIdRef.current = latestResult.id;
      appendHistory(latestResult);
    }
  }, [latestResult, appendHistory]);

  // モード切替時に他系統を必ず止める：
  //   - interpret に入る → useScanner.stopScanning()
  //   - interpret から出る → useInterpreter.stop()
  // useScanner は scanner.mode の useEffect 側でも stop が走るが、明示的にする
  useEffect(() => {
    if (currentMode === 'interpret') {
      // 画像系を停止
      stopScanning();
    } else {
      // 通訳系を停止
      interpreter.stop();
    }
    // stop / stopScanning は useCallback で安定。currentMode のみ依存
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode]);

  const handleChangeApiKey = () => {
    setShowSettings(false);
    setShowApiKeyEditor(true);
  };

  const handleSaveApiKey = (key: string) => {
    saveApiKey(key);
    setShowApiKeyEditor(false);
  };

  const isInterpret = currentMode === 'interpret';

  return (
    <>
      <div className={styles.root}>
        <StatusBar mode={currentMode} isScanning={isScanning} />

        <div className={styles.lensArea}>
          {isInterpret ? (
            <InterpretMode
              status={interpreter.status}
              finalOriginal={interpreter.finalOriginal}
              interimOriginal={interpreter.interimOriginal}
              pendingOriginal={interpreter.pendingOriginal}
              isBuffering={interpreter.isBuffering}
              japanese={interpreter.japanese}
              history={history}
              errorMessage={interpreter.errorMessage}
            />
          ) : (
            <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
              <Camera videoRef={videoRef} />
              <ScanLine active={isScanning} />
            </div>
          )}
        </div>

        <ModeSelector mode={currentMode} onChange={setCurrentMode} />

        <div className={styles.actions}>
          <ControlButton
            label="LOG"
            ariaLabel="履歴を開く"
            onClick={() => setShowHistory(true)}
          />

          {isInterpret ? (
            // 通訳モード：1個の START/STOP ボタンを SHOT 枠に流す
            <ShotButton
              onClick={() => {
                if (interpreter.status === 'listening' || interpreter.status === 'translating') {
                  interpreter.stop();
                } else if (interpreter.status === 'error') {
                  interpreter.reset();
                } else {
                  void interpreter.start();
                }
              }}
              isProcessing={false}
              disabled={!hasApiKey}
              label={
                interpreter.status === 'listening' || interpreter.status === 'translating'
                  ? 'STOP'
                  : interpreter.status === 'error'
                    ? 'RETRY'
                    : 'START'
              }
            />
          ) : (
            <>
              <ShotButton
                onClick={shoot}
                isProcessing={scannerStatus === 'processing'}
                disabled={!hasApiKey}
              />
              <LiveToggle
                isScanning={isScanning}
                onStart={startScanning}
                onStop={stopScanning}
                disabled={!hasApiKey}
              />
            </>
          )}

          <ControlButton
            label="CONF"
            ariaLabel="設定を開く"
            onClick={() => setShowSettings(true)}
          />
        </div>

        <BottomDecor />
      </div>

      {/* 結果パネルは画像系モード専用（v0.2 のまま）。通訳の結果は InterpretMode 内に出る。
          SCAN中だけ onStop を渡して ANALYZING… の右に「⏹ STOP」ボタンを出す（v0.3.1+） */}
      {!isInterpret && (
        <ResultPanel
          status={scannerStatus}
          result={latestResult}
          errorMessage={scannerError}
          currentMode={currentMode}
          history={history}
          {...(isScanning ? { onStop: stopScanning } : {})}
        />
      )}

      {/* 結果到着の瞬間だけ画面をフラッシュ（key で都度マウント） */}
      {!isInterpret && latestResult && <FlashEffect key={latestResult.id} />}

      {/* === モーダル群 === */}
      {showHistory && (
        <HistoryPanel
          history={history}
          onClear={clearHistory}
          onClose={() => setShowHistory(false)}
        />
      )}

      {showSettings && (
        <SettingsPanel
          settings={settings}
          onUpdateSettings={updateSettings}
          currentApiKey={apiKey}
          onChangeApiKey={handleChangeApiKey}
          onClearHistory={clearHistory}
          historyCount={history.length}
          appVersion={APP_VERSION}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!hasApiKey && (
        <ApiKeyModal
          dismissable={false}
          onSave={handleSaveApiKey}
        />
      )}
      {hasApiKey && showApiKeyEditor && (
        <ApiKeyModal
          dismissable
          initialValue={apiKey ?? ''}
          onSave={handleSaveApiKey}
          onDismiss={() => setShowApiKeyEditor(false)}
        />
      )}

      {!splashDone && <SplashScreen onComplete={handleSplashComplete} />}
    </>
  );
}
